import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";
import PhoneInput, { isValidPhoneNumber, type Value } from "react-phone-number-input";
import { PublicHeader } from "@/components/PublicHeader";
import { PublicFooter } from "@/components/PublicFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { FieldError } from "@/components/ui/field";
import { CheckCircle2, Loader2, Calendar, MapPin, LinkIcon } from "lucide-react";
import { IconFileText, IconLoader2, IconPhoneCall, IconUpload } from "@tabler/icons-react";
import {
  useLookupBookingByLinkMutation,
  useCancelBookingByLinkMutation,
  useCancelBookingApprovalMutation,
  useCreateBookingApprovalMutation,
  useGetBookingApprovalModeQuery,
  useGetBookingApprovalQuery,
  useVerifyBookingApprovalMutation,
} from "@/app/booking-flow/publicBookingFlowApi";
import {
  isCodeApproval,
  isPhoneApprovalPending,
  type BookingApprovalAction,
  type BookingApprovalChannelMode,
  type BookingApprovalStatus,
  type BookingDetails,
} from "@/app/booking-flow/publicBookingFlowTypes";
import {
  ApprovalCodeEntry,
  readVerifyFailure,
  type ApprovalCodeError,
} from "@/app/booking-flow/components/ApprovalCodeEntry";
import { BOOKING_TIME_ZONE } from "../booking-flow/bookingTimeZone";
import { documentsAvailability, useGetPublicBookingDocumentsQuery } from "@/app/document-upload/publicDocumentsApi";
import { LOCKED_OUT_MESSAGE, NOT_FOUND_MESSAGE } from "./manageLinkCopy";

const EXPIRED_LINK_MESSAGE =
  "This cancellation link is no longer valid. You can still manage your appointment by entering your phone number and confirmation number.";
const GENERIC_ERROR_MESSAGE = "Something went wrong. Please try again.";

// 410 is the link itself having lapsed, which is a different thing to tell someone than a wrong
// code: there is nothing they can retype here that will help, so the message has to send them to
// the manage page instead. See AmsBookingsController.ExpiredLink.
function errorMessageFor(error: unknown): string {
  const status = (error as { status?: number })?.status;
  if (status === 404) return NOT_FOUND_MESSAGE;
  if (status === 410) return EXPIRED_LINK_MESSAGE;
  if (status === 429) return LOCKED_OUT_MESSAGE;
  return GENERIC_ERROR_MESSAGE;
}

function isExpiredLink(error: unknown): boolean {
  return (error as { status?: number })?.status === 410;
}

/**
 * Whether the server asked for a phone approval first: 403 with code approvalRequired, which is what
 * the manage-link endpoints (and the documents grant) answer when BookingApprovalChallenge is on. That
 * answer is how this page learns the challenge is on; there is no flag endpoint to ask.
 */
export function isApprovalRequired(error: unknown): boolean {
  const failure = error as { status?: unknown; data?: { code?: unknown } } | null | undefined;
  return failure?.status === 403 && failure?.data?.code === "approvalRequired";
}

/**
 * Where the phone approval stands: none asked for, the phone form, waiting on the call, waiting for the
 * one-time code to be typed (BookingApproval:Mode=Code, AB#585; never polled), or how it ended.
 */
type ApprovalStep = "none" | "phone" | "waiting" | "approvalCode" | "rejected" | "expired" | "failed";

/**
 * The way to the documents list (AB#598). The confirmation text and email carry only this page's link,
 * so until online upload ships (AB#603) this is how a household gets from a message to what to bring.
 * It is rendered outside the page's states, so it shows whatever state the page is in: before the code
 * is entered, after lookup, after cancelling, and with a missing, expired or rejected link.
 */
export function WhatToBringLink() {
  return (
    <div className="w-full max-w-lg mx-auto">
      <Button asChild variant="outline" className="w-full h-12 text-base">
        <Link to="/booking/document-upload" data-testid="what-to-bring-link">
          <IconFileText className="w-5 h-5" aria-hidden="true" />
          What to bring to your appointment
        </Link>
      </Button>
    </div>
  );
}

/**
 * The way to the customer documents page (AB#774) for this appointment, with the same link token: the
 * confirmation number is asked for again there, since that page earns its own grant.
 */
export function UploadDocumentsLink({ token }: { token: string }) {
  return (
    <Button asChild variant="outline" className="w-full h-12 text-base">
      <Link to={`/booking/document-upload?t=${encodeURIComponent(token)}`} data-testid="upload-documents-link">
        <IconUpload className="w-5 h-5" aria-hidden="true" />
        Upload documents
      </Link>
    </Button>
  );
}

function formatCountdown(milliseconds: number): string {
  const remaining = Math.max(0, Math.ceil(milliseconds / 1000));
  return `${Math.floor(remaining / 60)}:${(remaining % 60).toString().padStart(2, "0")}`;
}

function formatAppointmentDateTime(startIso: string): string {
  return new Date(startIso).toLocaleString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: BOOKING_TIME_ZONE
  });
}

/**
 * Landing page for the cancellation link in a booking-details SMS (`/booking/cancel?t=…`).
 *
 * Holding the link is not enough to cancel anything: it resolves server-side to the phone number it
 * was minted for, and the 6-digit confirmation code from the message body is still required. That
 * keeps a forwarded or intercepted link harmless, and it is also why this page never cancels on
 * load — a GET that cancelled would fire the moment any messaging app, link scanner, or mail
 * preview fetched the URL, with no one having tapped anything.
 *
 * With the server's phone challenge on (BookingApprovalChallenge), the lookup and the cancel each
 * answer 403 approvalRequired until the phone on the appointment has said yes, exactly as the
 * documents page (/booking/document-upload) is answered: the page then asks for the phone number, has
 * the server call it (POST api/ams/booking-approvals, ViewBooking for the lookup, CancelBooking for the
 * cancel), polls the approval, and once it is approved asks again with its id. The number typed for
 * the view is reused for the cancel. With the challenge off the 403 never comes and the page works as
 * it always has.
 */
export default function BookingCancelPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("t") ?? "";

  const [lookupBookingByLink, { isLoading: lookupLoading }] = useLookupBookingByLinkMutation();
  const [cancelBookingByLink, { isLoading: cancelLoading }] = useCancelBookingByLinkMutation();
  const [createApproval, { isLoading: creatingApproval }] = useCreateBookingApprovalMutation();
  const [withdrawApproval] = useCancelBookingApprovalMutation();
  const [verifyApproval, { isLoading: verifying }] = useVerifyBookingApprovalMutation();

  const [codeInput, setCodeInput] = useState("");
  const [reasonInput, setReasonInput] = useState("");
  const [lookupError, setLookupError] = useState("");
  const [cancelError, setCancelError] = useState("");
  const [linkExpired, setLinkExpired] = useState(false);
  const [booking, setBooking] = useState<BookingDetails | null>(null);
  const [cancelledAt, setCancelledAt] = useState<string | null>(null);

  // The phone approval, when the server asks for one.
  const [approvalStep, setApprovalStep] = useState<ApprovalStep>("none");
  const [approvalAction, setApprovalAction] = useState<BookingApprovalAction>("ViewBooking");
  const [phoneInput, setPhoneInput] = useState<Value | undefined>();
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [approvalError, setApprovalError] = useState("");
  const [approvalPhone, setApprovalPhone] = useState<string | null>(null);
  const [approvalId, setApprovalId] = useState<string | null>(null);
  const [pollSeconds, setPollSeconds] = useState(3);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const usedApproval = useRef<string | null>(null);

  // Code mode (AB#585): how the current approval is answered, from its 202 (the mode query only
  // words the phone form before there is one), and the verify endpoint's answer, which stands in for
  // the poll's in the effect below.
  const { data: approvalModeInfo } = useGetBookingApprovalModeQuery();
  const [approvalChannel, setApprovalChannel] = useState<BookingApprovalChannelMode | null>(null);
  const codeChannel = (approvalChannel ?? approvalModeInfo?.approvalMode) === "code";
  const [verified, setVerified] = useState<BookingApprovalStatus | null>(null);
  const [codeError, setCodeError] = useState<ApprovalCodeError | undefined>(undefined);

  const waiting = approvalStep === "waiting";
  const enteringCode = approvalStep === "approvalCode";
  const locallyExpired = expiresAt !== null && now > expiresAt;
  const phoneValid = !!phoneInput && isValidPhoneNumber(phoneInput);

  useEffect(() => {
    if (!waiting && !enteringCode) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [waiting, enteringCode]);

  const { data: approval, isError: approvalPollError } = useGetBookingApprovalQuery(
    waiting && approvalId ? approvalId : skipToken,
    { pollingInterval: waiting && !locallyExpired ? pollSeconds * 1000 : 0, skipPollingIfUnfocused: false },
  );

  // Whether customer documents are on (AB#774), from the documents list's answer; there is no public
  // flag endpoint. Only decides whether the found appointment offers the Upload documents link.
  const documentsOn = documentsAvailability(useGetPublicBookingDocumentsQuery(undefined, { refetchOnMountOrArgChange: true })) === "on";

  const code = codeInput.trim();
  const canSubmit = token !== "" && code !== "";

  const cancellationReason = reasonInput.trim() || undefined;

  // Has the server call the phone on the appointment for `action`, then waits for the answer. The
  // server answers a right and a wrong code alike here, so a code that matched nothing simply never
  // gets approved and reads as no answer.
  // Reports whether the request went through. A failed resend passes stayOnFailure, so the code view
  // and the still-valid approval behind it survive with the error shown inline.
  const startApproval = async (
    phoneNumber: string,
    action: BookingApprovalAction,
    stayOnFailure = false,
  ): Promise<boolean> => {
    setApprovalAction(action);
    setApprovalError("");
    try {
      const result = await createApproval({
        phoneNumber,
        confirmationCode: code,
        action,
        cancellationReason: action === "CancelBooking" ? cancellationReason : undefined,
      }).unwrap();
      if (!isPhoneApprovalPending(result)) {
        // The challenge was switched off in between, and the action is already done.
        setApprovalStep("none");
        if (result.booking) setBooking(result.booking);
        if (result.cancelledAt) setCancelledAt(result.cancelledAt);
        return true;
      }
      setApprovalPhone(phoneNumber);
      setApprovalId(result.approvalId);
      setPollSeconds(result.pollIntervalSeconds);
      setExpiresAt(Date.parse(result.expiresAt));
      setNow(Date.now());
      setApprovalChannel(isCodeApproval(result) ? "code" : "call");
      setVerified(null);
      setCodeError(undefined);
      setApprovalStep(isCodeApproval(result) ? "approvalCode" : "waiting");
      return true;
    } catch (error) {
      if (!stayOnFailure) setApprovalStep("phone");
      setApprovalError((error as { status?: number })?.status === 429 ? LOCKED_OUT_MESSAGE : GENERIC_ERROR_MESSAGE);
      return false;
    }
  };

  // An approval presented and refused means it lapsed between the poll and the call.
  const askForApproval = (action: BookingApprovalAction, presented: string | undefined) => {
    setApprovalAction(action);
    if (presented) {
      setApprovalStep("expired");
    } else if (approvalPhone) {
      void startApproval(approvalPhone, action);
    } else {
      setApprovalStep("phone");
    }
  };

  const requestLookup = async (withApproval?: string): Promise<void> => {
    setLookupError("");
    try {
      setBooking(await lookupBookingByLink({ token, confirmationCode: code, approvalId: withApproval }).unwrap());
      setApprovalStep("none");
    } catch (error) {
      if (isApprovalRequired(error)) {
        askForApproval("ViewBooking", withApproval);
        return;
      }
      setApprovalStep("none");
      setLinkExpired(isExpiredLink(error));
      setLookupError(errorMessageFor(error));
    }
  };

  const requestCancel = async (withApproval?: string): Promise<void> => {
    setCancelError("");
    try {
      const result = await cancelBookingByLink({
        token,
        confirmationCode: code,
        cancellationReason,
        approvalId: withApproval,
      }).unwrap();

      setCancelledAt(result.cancelledAt);
      setApprovalStep("none");
    } catch (error) {
      if (isApprovalRequired(error)) {
        askForApproval("CancelBooking", withApproval);
        return;
      }
      setApprovalStep("none");
      setCancelError(errorMessageFor(error));
    }
  };

  // The poll's answer, or in code mode the verify endpoint's. Approved: ask again at once with it.
  // Anything else final: say so.
  useEffect(() => {
    if (!waiting && !enteringCode) return;
    if (approvalPollError || locallyExpired) {
      setApprovalStep("expired");
      return;
    }
    const answer = enteringCode ? verified : approval;
    if (!answer || answer.state === "pending") return;
    if (answer.state === "approved") {
      if (usedApproval.current === answer.approvalId) return;
      usedApproval.current = answer.approvalId;
      void (approvalAction === "CancelBooking" ? requestCancel(answer.approvalId) : requestLookup(answer.approvalId));
      return;
    }
    setApprovalStep(answer.state === "rejected" ? "rejected" : answer.state === "failed" ? "failed" : "expired");
    // requestLookup and requestCancel are recreated each render and only read current state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approval, verified, approvalPollError, locallyExpired, waiting, enteringCode]);

  // Code mode: a 200 goes to the effect above; a wrong code stays here with the guesses left; an
  // approval that is over (410, 404, or the last guess used) shows the expired view; a 409 means
  // approvals went back to calls under the page, so it waits for the call instead.
  const verifyCode = async (typed: string) => {
    if (!approvalId) return;
    setCodeError(undefined);
    try {
      setVerified(await verifyApproval({ approvalId, code: typed }).unwrap());
    } catch (error) {
      const failure = readVerifyFailure(error);
      if (failure.kind === "gone") {
        setApprovalStep("expired");
      } else if (failure.kind === "notCodeMode") {
        setApprovalChannel("call");
        setApprovalStep("waiting");
      } else {
        setCodeError(failure);
      }
    }
  };

  // "Send a new code": the request the phone form makes, again, to the number already given, then the
  // old approval withdrawn (the new one has already superseded it; this only tidies up). Withdrawn only
  // once the new request succeeds: a refused one (a 429) leaves the old code usable, error shown inline.
  const resendCode = async () => {
    if (!approvalPhone) return;
    const previous = approvalId;
    setCodeError(undefined);
    if ((await startApproval(approvalPhone, approvalAction, true)) && previous) {
      void withdrawApproval(previous);
    }
  };

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    await requestLookup();
  };

  const handleCancel = async (e: React.FormEvent) => {
    e.preventDefault();
    await requestCancel();
  };

  const handlePhone = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneValid) {
      setPhoneTouched(true);
      return;
    }
    await startApproval(phoneInput!, approvalAction);
  };

  // Withdrawn, not just abandoned: an unanswered approval call is tried again, and nobody is waiting.
  const stopWaiting = () => {
    const withdrawing = approvalId;
    setApprovalId(null);
    setExpiresAt(null);
    setApprovalStep("phone");
    if (withdrawing) void withdrawApproval(withdrawing);
  };

  // Back to where the person was: the code form for a view, the appointment for a cancel.
  const startOver = () => {
    setApprovalId(null);
    setExpiresAt(null);
    setApprovalError("");
    setApprovalStep("none");
  };

  const manageLink = (
    <Button asChild variant="ghost" className="w-full text-slate-400">
      <Link to="/booking/manage">Manage Your Appointment</Link>
    </Button>
  );

  // A link with no token at all never came from one of our messages — most likely someone opened a
  // truncated URL. There is nothing to look up, so the page goes straight to the manage page rather
  // than asking for a code it could not use.
  const missingToken = token === "";

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 flex flex-col">
      <PublicHeader />

      <main className="flex-1 flex flex-col items-center justify-center gap-4 px-4 sm:px-6 lg:px-8 py-8">
        {cancelledAt ? (
          <Card className="w-full max-w-lg mx-auto border-none shadow-none sm:border-solid sm:shadow-sm">
            <CardHeader className="text-center px-4 space-y-4">
              <div className="mx-auto w-16 h-16 bg-emerald-100 flex items-center justify-center rounded-full">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <CardTitle className="text-2xl font-semibold text-slate-900">Appointment Cancelled</CardTitle>
              <CardDescription className="text-slate-500 text-base">
                If you need energy assistance, please schedule a new appointment when the next set of appointments becomes available.
              </CardDescription>
            </CardHeader>

            <CardFooter className="px-4 pb-6">
              <Button asChild variant="ghost" className="w-full text-slate-400">
                <Link to="/booking">Back to Scheduler Home</Link>
              </Button>
            </CardFooter>
          </Card>
        ) : missingToken || linkExpired ? (
          <Card className="w-full max-w-lg mx-auto border-none shadow-none sm:border-solid sm:shadow-sm">
            <CardHeader className="text-center px-4 space-y-4">
              <div className="mx-auto w-16 h-16 bg-slate-100 flex items-center justify-center rounded-full">
                <LinkIcon className="w-8 h-8 text-slate-400" />
              </div>
              <CardTitle className="text-2xl font-semibold text-slate-900">Link No Longer Valid</CardTitle>
              <CardDescription className="text-slate-500 text-base">
                {EXPIRED_LINK_MESSAGE}
              </CardDescription>
            </CardHeader>

            <CardFooter className="px-4 pb-6 flex flex-col gap-2">
              {manageLink}
              <Button asChild variant="ghost" className="w-full text-slate-400">
                <Link to="/booking">Back to Scheduler Home</Link>
              </Button>
            </CardFooter>
          </Card>
        ) : enteringCode ? (
          <ApprovalCodeEntry
            key={`code:${approvalId}`}
            className="w-full max-w-lg mx-auto border-none shadow-none sm:border-solid sm:shadow-sm"
            action={approvalAction}
            expiresAt={expiresAt}
            nowMs={now}
            onVerify={verifyCode}
            onResend={approvalPhone ? () => void resendCode() : undefined}
            onStartOver={stopWaiting}
            error={codeError ?? (approvalError !== "" ? { kind: "other", message: approvalError } : undefined)}
            busy={verifying || creatingApproval || lookupLoading || cancelLoading}
          />
        ) : waiting ? (
          <Card key="waiting" className="w-full max-w-lg mx-auto border-none shadow-none sm:border-solid sm:shadow-sm" data-testid="approval-waiting">
            <CardHeader className="text-center px-4 space-y-4">
              <div className="mx-auto w-16 h-16 bg-sky-100 flex items-center justify-center rounded-full">
                <IconPhoneCall className="w-8 h-8 text-sky-600" aria-hidden="true" />
              </div>
              <CardTitle className="text-2xl font-semibold text-slate-900">Answer Your Phone</CardTitle>
              <CardDescription className="text-slate-500 text-base">
                We're calling the phone number on this appointment. When you answer, press <strong>1</strong> to
                {approvalAction === "CancelBooking" ? " cancel it" : " see your appointment details"}, or{" "}
                <strong>2</strong> to deny.
              </CardDescription>
            </CardHeader>

            <CardContent className="flex flex-col items-center gap-3" aria-live="polite">
              <IconLoader2 className="w-6 h-6 animate-spin text-slate-400" aria-hidden="true" />
              {expiresAt !== null && (
                <p className="text-sm text-slate-500">
                  This request expires in {formatCountdown(expiresAt - now)}
                </p>
              )}
            </CardContent>

            <CardFooter className="px-4 pb-6">
              <Button type="button" variant="ghost" className="w-full text-slate-400" onClick={stopWaiting}>
                Cancel this request
              </Button>
            </CardFooter>
          </Card>
        ) : approvalStep === "rejected" || approvalStep === "expired" || approvalStep === "failed" ? (
          <Card key="resolved" className="w-full max-w-lg mx-auto border-none shadow-none sm:border-solid sm:shadow-sm" data-testid="approval-result">
            <CardHeader className="text-center px-4">
              <CardTitle className="text-2xl font-semibold text-slate-900">
                {approvalStep === "rejected"
                  ? "Request Denied"
                  : approvalStep === "failed"
                    ? "Something Went Wrong"
                    : approvalChannel === "code"
                      ? "Code No Longer Valid"
                      : "No Response"}
              </CardTitle>
              <CardDescription className="text-slate-500 text-base">
                {approvalStep === "rejected"
                  ? "The request was denied from the phone on this appointment. Nothing was changed."
                  : approvalStep === "failed"
                    ? "The request was approved, but we could not finish it. Please call us so we can help."
                    : approvalChannel === "code"
                      ? "That code can no longer be used. Check the phone number and confirmation number, then try again."
                      : "We did not get an answer in time. Check the phone number and confirmation number, then try again."}
              </CardDescription>
            </CardHeader>

            <CardFooter className="px-4 pb-6">
              <Button
                type="button"
                className="w-full text-base h-12 bg-[#009681] hover:bg-[#007e6c] active:bg-[#006b5b]"
                onClick={startOver}
              >
                Try Again
              </Button>
            </CardFooter>
          </Card>
        ) : approvalStep === "phone" ? (
          <Card key="phone" className="w-full max-w-lg mx-auto border-none shadow-none sm:border-solid sm:shadow-sm">
            <CardHeader className="text-center px-4">
              <CardTitle className="text-2xl font-semibold text-slate-900">Confirm It's You</CardTitle>
              <CardDescription className="text-slate-500">
                {codeChannel ? (
                  "For your security, we'll send a six-digit code to the phone number on this appointment. Enter that phone number."
                ) : (
                  <>
                    For your security, we'll call the phone number on this appointment. Enter that phone number, then
                    answer the call and press 1.
                  </>
                )}
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form className="space-y-4" id="booking-phone-form" onSubmit={handlePhone}>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <PhoneInput
                    id="phone"
                    name="phone"
                    placeholder="Enter phone number"
                    defaultCountry="US"
                    value={phoneInput}
                    onChange={(value) => setPhoneInput(value)}
                    onBlur={() => setPhoneTouched(true)}
                    className="border-input [&.PhoneInput--focus]:border-ring [&.PhoneInput--focus]:ring-ring/50 [&.PhoneInput--focus]:ring-[3px] flex h-9 w-full min-w-0 items-center gap-2 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs outline-none md:text-sm"
                    numberInputProps={{
                      className: "placeholder:text-muted-foreground min-w-0 flex-1 border-0 bg-transparent p-0 text-base shadow-none outline-none md:text-sm",
                    }}
                  />
                  {phoneTouched && !phoneValid && <FieldError>Enter a valid phone number.</FieldError>}
                </div>
                {approvalError !== "" && <FieldError>{approvalError}</FieldError>}
              </form>
            </CardContent>

            <CardFooter className="px-4 pb-6 flex flex-col gap-2">
              <Button
                form="booking-phone-form"
                type="submit"
                className="w-full text-base h-12 bg-[#009681] hover:bg-[#007e6c] active:bg-[#006b5b]"
                disabled={creatingApproval || lookupLoading || cancelLoading}
              >
                {codeChannel ? (creatingApproval ? "Sending…" : "Send Code") : creatingApproval ? "Calling…" : "Call Me"}
              </Button>
              <Button type="button" variant="ghost" className="w-full text-slate-400" onClick={startOver}>
                Back
              </Button>
            </CardFooter>
          </Card>
        ) : booking ? (
          <Card className="w-full max-w-lg mx-auto border-none shadow-none sm:border-solid sm:shadow-sm">
            <CardHeader className="text-center px-4">
              <CardTitle className="text-2xl font-semibold text-slate-900">Your Appointment</CardTitle>
              <CardDescription className="text-slate-500">
                Cancelling this appointment can't be undone. You'd need to book a new one.
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              <div className="bg-slate-50 p-5 rounded-lg border space-y-4">
                <div className="flex gap-3">
                  <Calendar className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
                  <div className="flex flex-col">
                    <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Appointment</span>
                    <span className="font-medium text-sm mt-0.5">{formatAppointmentDateTime(booking.start)}</span>
                  </div>
                </div>

                {(booking.siteName || booking.location) && (
                  <div className="flex gap-3">
                    <MapPin className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
                    <div className="flex flex-col">
                      <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Location</span>
                      <span className="font-medium text-sm mt-0.5">
                        {booking.siteName}
                        {booking.siteName && booking.location ? " — " : ""}
                        {booking.location}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <form className="space-y-2" id="booking-cancel-form" onSubmit={handleCancel}>
                <Label htmlFor="reason">Reason for Cancelling</Label>
                <Textarea
                  id="reason"
                  name="reason"
                  placeholder="Optional"
                  value={reasonInput}
                  onChange={(e) => setReasonInput(e.target.value)}
                />
                {cancelError !== "" && <FieldError>{cancelError}</FieldError>}
              </form>

              {documentsOn && <UploadDocumentsLink token={token} />}
            </CardContent>

            <CardFooter className="px-4 pb-6 flex flex-col gap-2">
              <Button
                form="booking-cancel-form"
                type="submit"
                variant="destructive"
                className="w-full text-base h-12"
                disabled={cancelLoading || creatingApproval}
              >
                {cancelLoading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Cancelling…
                  </span>
                ) : (
                  "Cancel Appointment"
                )}
              </Button>
              <Button type="button" variant="ghost" className="w-full text-slate-400" onClick={() => setBooking(null)}>
                Keep Appointment
              </Button>
            </CardFooter>
          </Card>
        ) : (
          <Card className="w-full max-w-lg mx-auto border-none shadow-none sm:border-solid sm:shadow-sm">
            <CardHeader className="text-center px-4">
              <CardTitle className="text-2xl font-semibold text-slate-900">Cancel Your Appointment</CardTitle>
              <CardDescription className="text-slate-500">
                For your security, enter the 6-digit confirmation number from your appointment text message.
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form className="space-y-4" id="booking-code-form" onSubmit={handleLookup}>
                <div className="space-y-2">
                  <Label htmlFor="code">Confirmation Number <span aria-hidden="true">*</span></Label>
                  <Input
                    id="code"
                    name="code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="6-digit confirmation number"
                    value={codeInput}
                    onChange={(e) => setCodeInput(e.target.value)}
                    required
                  />
                </div>

                {lookupError !== "" && <FieldError>{lookupError}</FieldError>}
              </form>
            </CardContent>

            <CardFooter className="px-4 pb-6 flex flex-col gap-2">
              <Button
                form="booking-code-form"
                type="submit"
                className="w-full text-base h-12 bg-[#009681] hover:bg-[#007e6c] active:bg-[#006b5b]"
                disabled={lookupLoading || !canSubmit}
              >
                {lookupLoading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Checking…
                  </span>
                ) : (
                  "Continue"
                )}
              </Button>
              <Button asChild variant="ghost" className="w-full text-slate-400">
                <Link to="/booking">Back to Scheduler Home</Link>
              </Button>
            </CardFooter>
          </Card>
        )}

        <WhatToBringLink />
      </main>

      <PublicFooter>Multi-Service Center &nbsp;·&nbsp; Energy Assistance</PublicFooter>
    </div>
  );
}
